<template>
  <CSidebar
    fixed
    class="preika-sidebar"
    :minimize="minimize"
    :show="show"
    @update:show="value => $store.commit('set', ['sidebarShow', value])"
  >
    <CSidebarBrand>
      <div
        class="preika-sidebar-brand"
        role="button"
        tabindex="0"
        aria-label="ダッシュボードへ移動"
        @click="redirectToPage"
        @keyup.enter="redirectToPage"
      >
        <img src="/img/avatars/preika.png" :alt="productName" class="preika-sidebar-brand__logo">
        <span class="preika-sidebar-brand__copy" aria-hidden="true">
          <span class="preika-sidebar-brand__name">{{ brand }}</span>
          <span class="preika-sidebar-brand__area">{{ productArea }}</span>
        </span>
        <!-- 畳んだときの印。語を割らずに、別の1文字を出す。 -->
        <span class="preika-sidebar-brand__mark" aria-hidden="true">P</span>
      </div>
    </CSidebarBrand>

    <CRenderFunction flat :content-to-render="navItems"/>

    <div class="preika-sidebar-meta">
      <span class="preika-sidebar-meta__dot"></span>
      <span class="preika-sidebar-meta__copy">{{ workspaceLabel }}</span>
    </div>
  </CSidebar>
</template>

<script>
import axios from 'axios'
import nav from './_nav_admin'
import { BRAND, PRODUCT_AREA, PRODUCT_NAME } from '@/common/productName'
import {
  canManageProviderRoles,
  canOpenProviderAccessManagement,
  canOpenTenantAccessManagement,
  filterNavigation
} from '../utils/accessPolicy'

export default {
  name: 'TheAdminSidebar',
  nav,

  props: {
    accessContext: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      navItems: []
    }
  },

  computed: {
    /** 売るときの正式な表記。**1か所から取る。** */
    /** 銘の「勤怠」の部分。**シリーズの中のどれかを示す。** */
    /** シリーズの名前。 */
    brand () {
      return BRAND
    },

    productArea () {
      return PRODUCT_AREA
    },

    productName () {
      return PRODUCT_NAME
    },

    show () { return this.$store.state.sidebarShow },
    minimize () { return this.$store.state.sidebarMinimize },
    sidebarBadgeRefreshTick () { return this.$store.state.sidebarBadgeRefreshTick },
    workspaceLabel () {
      const roles = this.accessContext.roles || []
      /*
       * **運営側だけ、シリーズの名前で出す。**買った会社の人は入れない
       * 画面へ入っているときの表示なので、ここに勤怠と書くとかえって
       * 分からなくなる（給与や評価の運営もここへ来る）。
       */
      if (roles.includes('PLATFORM_SUPER_ADMIN')) return 'PREIKA 運営（緊急）'
      if (roles.includes('PLATFORM_OPERATIONS_ADMIN')) return 'PREIKA 運営'
      if (roles.includes('PLATFORM_SUPPORT_VIEWER')) return 'PREIKA サポート'
      /*
       * ここから下は買った会社の人が見る。**製品の名前と役割**にする。
       *
       * 英語をやめたのは、Clerk が英語では「受付係」に読めるため。
       * 日本語の「一般」とはずれていた（2026-08-27 利用者の指摘）。
       */
      if (roles.includes('TENANT_OWNER')) return this.productName + '　オーナー'
      if (roles.includes('TENANT_SYSTEM_ADMIN')) return this.productName + '　システム管理'
      if (roles.includes('EXECUTIVE_ADMIN')) return this.productName + '　管理'
      if (roles.includes('CLERK')) return this.productName + '　担当'
      return this.productName
    }
  },

  watch: {
    accessContext: {
      immediate: true,
      deep: true,
      handler () {
        this.rebuildNavigation()
      }
    },
    sidebarBadgeRefreshTick () { this.fetchSidebarBadges() }
  },

  mounted () {
    this.fetchSidebarBadges()
  },

  methods: {
    redirectToPage () {
      if (this.$route.path !== '/dashboard') this.$router.push('/dashboard')
    },

    rebuildNavigation () {
      const draft = JSON.parse(JSON.stringify(nav))
      const root = draft[0]
      if (root && Array.isArray(root._children)) {
        // **運営（PREIKA側）のものと、買った会社のものを分ける。**
        // ひとつの「権限管理」にまとめていたころは、運営にしか出ない行と
        // 会社の管理者に出る行が同じ見出しの下に並んでいて、
        // 自分が触ってよいものかが見出しから読めなかった。
        const 運営のもの = []
        const 自社のもの = []

        if (canOpenProviderAccessManagement(this.accessContext)) {
          運営のもの.push({
            _name: 'CSidebarNavItem',
            name: '契約企業管理',
            to: '/platform/access-management',
            icon: 'cil-building'
          })
        }
        if (canManageProviderRoles(this.accessContext)) {
          運営のもの.push({
            _name: 'CSidebarNavItem',
            name: 'PREIKA運営権限管理',
            to: '/platform/provider-role-management',
            icon: 'cil-shield-alt'
          })
          /*
           * **経路を作っただけでは、誰もたどり着けない。**お知らせは
           * 出す人がいて初めて出るので、入口が無いと「出せる仕組みは
           * あるが1件も出ない」で終わる。
           */
          運営のもの.push({
            _name: 'CSidebarNavItem',
            name: '運営からのお知らせ',
            to: '/platform/notices',
            icon: 'cil-bell'
          })
          /*
           * **在庫が切れたことに気づける場所。**祝日マスタは
           * 読む側しか無く、何年先まで入っているかを誰も見ていな
           * かった。入口をここへ置いて、開けば残りが見えるようにする。
           */
          運営のもの.push({
            _name: 'CSidebarNavItem',
            name: '祝日マスタ',
            to: '/platform/national-holidays',
            icon: 'cil-calendar'
          })
        }
        if (canOpenTenantAccessManagement(this.accessContext)) {
          自社のもの.push({
            _name: 'CSidebarNavItem',
            name: '自社権限管理',
            to: '/admin/tenant-access-management',
            icon: 'cil-people'
          })
          /*
           * **端末を失くした社員を救う道。**
           *
           * ここに出さないと、画面はあるのに URL を直打ちするしか
           * 開けない。2026-08-22 まで「2要素を失った人を誰も元に
           * 戻せない」状態だったのを直したのに、その入口が無い。
           *
           * 出す条件は自社権限管理と同じ。back も
           * /tenant-access-management を TENANT_OWNER と
           * TENANT_SYSTEM_ADMIN だけに絞っている
           * （ApiAccessPolicyService）。
           */
          自社のもの.push({
            _name: 'CSidebarNavItem',
            name: '2要素認証の復旧',
            to: '/admin/mfa-recovery',
            icon: 'cil-lock-unlocked'
          })
        }
        /*
         * **いちばん下へ足す。**
         *
         * ダッシュボードの直下（上から2番目）へ入れていたころは、
         * 会社の管理者にとって<b>いちばん触らないものが最初に来て</b>、
         * 毎月出す勤務表や経費精算が下へ押し下げられていた。
         * 権限や契約は一度決めたらしばらく触らない。
         */
        if (自社のもの.length) {
          root._children.push(
            { _name: 'CSidebarNavTitle', _children: ['自社の権限'] },
            ...自社のもの
          )
        }
        if (運営のもの.length) {
          root._children.push(
            { _name: 'CSidebarNavTitle', _children: ['PREIKA運営'] },
            ...運営のもの
          )
        }
      }

      this.navItems = filterNavigation(draft, this.accessContext)
      this.$nextTick(this.fetchSidebarBadges)
    },

    fetchSidebarBadges () {
      if (this.findNavItemByTo(this.navItems, '/submission_approval/report_list')) {
        this.fetchSubmissionListCount()
      }
      if (this.findNavItemByTo(this.navItems, '/submission_approval/submission_management')) {
        this.fetchApprovalPendingCount()
      }
    },

    fetchSubmissionListCount () {
      const origin = process.env.VUE_APP_API_ORIGIN
      return axios.get(origin + '/submission/list')
        .then(response => {
          const count = Array.isArray(response.data) ? response.data.length : 0
          this.setNavBadgeByTo('/submission_approval/report_list', count)
        })
        .catch(error => {
          axios.post(`${origin}/error/frontError`, { error: String(error) }, { headers: { 'Content-Type': 'application/json' } }).then(() => {}).catch(() => {})
          this.setNavBadgeByTo('/submission_approval/report_list', 0)
        })
    },

    setNavBadgeByTo (to, count) {
      const item = this.findNavItemByTo(this.navItems, to)
      if (!item) return
      const numericCount = Number(count)
      if (!Number.isFinite(numericCount) || numericCount <= 0) {
        this.$set(item, 'badge', null)
        return
      }
      this.$set(item, 'badge', {
        color: 'danger',
        text: numericCount > 99 ? '99+' : String(numericCount)
      })
    },

    findNavItemByTo (items, to) {
      if (!Array.isArray(items)) return null
      for (let index = 0; index < items.length; index += 1) {
        const item = items[index]
        if (item && item.to === to) return item
        if (item && Array.isArray(item._children)) {
          const found = this.findNavItemByTo(item._children, to)
          if (found) return found
        }
      }
      return null
    },

    fetchApprovalPendingCount () {
      const origin = process.env.VUE_APP_API_ORIGIN
      return axios.get(origin + '/document/approval/list')
        .then(response => {
          const count = Array.isArray(response.data) ? response.data.length : 0
          this.setNavBadgeByTo('/submission_approval/submission_management', count)
        })
        .catch(error => {
          axios.post(`${origin}/error/frontError`, { error: String(error) }, { headers: { 'Content-Type': 'application/json' } }).then(() => {}).catch(() => {})
          this.setNavBadgeByTo('/submission_approval/submission_management', 0)
        })
    }
  }
}
</script>
